/**
 * The Telegram Mini App surface, typed by hand.
 *
 * Two things here are load-bearing:
 *
 *  - `initData` is the credential. The numeric user id inside it is the same id
 *    the 2018 export recorded, which is why the app can show someone their own
 *    eight years without asking who they are.
 *  - Fullscreen (Bot API 8.0) puts content under the notch and under Telegram's
 *    own header, so both `safeAreaInset` and `contentSafeAreaInset` have to be
 *    tracked and re-read on every change. Skipping the second one is the classic
 *    bug: the layout looks right on the emulator and hides the first line of
 *    text behind the header on a real phone.
 */

type Insets = { top: number; bottom: number; left: number; right: number };

type ThemeParams = Record<string, string | undefined>;

interface WebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name?: string; username?: string } };
  version: string;
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  isFullscreen?: boolean;
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  ready(): void;
  expand(): void;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  disableVerticalSwipes?(): void;
  setHeaderColor?(color: string): void;
  onEvent(event: string, handler: (payload?: unknown) => void): void;
  offEvent(event: string, handler: (payload?: unknown) => void): void;
  HapticFeedback?: { selectionChanged(): void; impactOccurred(style: string): void };
  openTelegramLink?(url: string): void;
}

const ZERO: Insets = { top: 0, bottom: 0, left: 0, right: 0 };

export function webApp(): WebApp | null {
  return (window as unknown as { Telegram?: { WebApp: WebApp } }).Telegram?.WebApp ?? null;
}

export function insideTelegram(): boolean {
  return Boolean(webApp()?.initData);
}

/** Bot API versions are dotted strings, compared numerically, not lexically. */
export function supports(minVersion: string): boolean {
  const current = webApp()?.version ?? "0";
  const a = current.split(".").map(Number);
  const b = minVersion.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff > 0;
  }
  return true;
}

/**
 * Take only the light/dark signal from Telegram, not its palette.
 *
 * Adopting `themeParams` wholesale makes the app dissolve into whatever theme
 * the viewer runs, which costs it any identity of its own. The colours are
 * defined in styles.css and keyed off `data-scheme`; Telegram decides which of
 * the two applies, and the header is matched to our background so the chrome
 * does not fight the page.
 */
function applyTheme(app: WebApp) {
  const root = document.documentElement;
  root.dataset.scheme = app.colorScheme;
  // Read --bg only after the scheme is stamped, and never pin it as an inline
  // style on <body>: an inline background outranks the stylesheet permanently,
  // so the next scheme change would repaint the text and leave the page behind
  // it — light text on a light ground.
  const bg = getComputedStyle(root).getPropertyValue("--bg").trim();
  if (bg && supports("6.1")) app.setHeaderColor?.(bg);
}

/** Combined insets: the device's notch plus whatever Telegram's chrome covers. */
function applyInsets(app: WebApp) {
  const device = app.safeAreaInset ?? ZERO;
  const content = app.contentSafeAreaInset ?? ZERO;
  const root = document.documentElement;
  root.style.setProperty("--inset-top", `${device.top + content.top}px`);
  root.style.setProperty("--inset-bottom", `${device.bottom + content.bottom}px`);
  root.style.setProperty("--inset-left", `${device.left + content.left}px`);
  root.style.setProperty("--inset-right", `${device.right + content.right}px`);
}

/**
 * Bring the app up: theme, fullscreen, insets, and the listeners that keep them
 * correct when the user rotates the phone or leaves fullscreen.
 *
 * Returns a teardown function.
 */
export function initTelegram(onChange: () => void): () => void {
  const app = webApp();
  if (!app) return () => {};

  app.ready();
  app.expand();
  applyTheme(app);
  applyInsets(app);

  // Fullscreen arrived in 8.0; on anything older `expand()` is as far as we go.
  if (supports("8.0") && app.requestFullscreen) {
    app.requestFullscreen();
    app.disableVerticalSwipes?.();
  }

  const refresh = () => {
    applyTheme(app);
    applyInsets(app);
    onChange();
  };
  const onFullscreenFailed = () => {
    // Telegram refuses fullscreen on some clients and in landscape. Not fatal —
    // the expanded layout is still usable, so just re-measure.
    applyInsets(app);
    onChange();
  };

  const events = [
    "themeChanged",
    "viewportChanged",
    "fullscreenChanged",
    "safeAreaChanged",
    "contentSafeAreaChanged",
  ];
  events.forEach((e) => app.onEvent(e, refresh));
  app.onEvent("fullscreenFailed", onFullscreenFailed);

  return () => {
    events.forEach((e) => app.offEvent(e, refresh));
    app.offEvent("fullscreenFailed", onFullscreenFailed);
  };
}

export function tap() {
  // Haptics landed in 6.1. Calling it on an older client is harmless but logs a
  // warning on every tap, which buries real errors in the console.
  if (supports("6.1")) webApp()?.HapticFeedback?.selectionChanged();
}

export function openChannel(handle: string) {
  const app = webApp();
  const url = `https://t.me/${handle}`;
  if (app?.openTelegramLink) app.openTelegramLink(url);
  else window.open(url, "_blank");
}
